import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface ConditionalFieldRule {
  field: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan' | 'isEmpty' | 'isNotEmpty';
  value?: any;
}

export interface ConditionalFieldConfig {
  rules: ConditionalFieldRule[];
  logic?: 'and' | 'or';
}

interface ConditionalFieldsProps {
  condition: ConditionalFieldConfig;
  formData: Record<string, any>;
  children: React.ReactNode;
  className?: string;
  animationDuration?: number;
}

const ConditionalFields: React.FC<ConditionalFieldsProps> = ({
  condition,
  formData,
  children,
  className = '',
  animationDuration = 0.3
}) => {
  const evaluateRule = (rule: ConditionalFieldRule): boolean => {
    const fieldValue = formData[rule.field];
    
    switch (rule.operator) {
      case 'equals':
        return fieldValue === rule.value;
      case 'notEquals':
        return fieldValue !== rule.value;
      case 'contains':
        return typeof fieldValue === 'string' && fieldValue.includes(rule.value);
      case 'greaterThan':
        return typeof fieldValue === 'number' && fieldValue > rule.value;
      case 'lessThan':
        return typeof fieldValue === 'number' && fieldValue < rule.value;
      case 'isEmpty':
        return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0) || 
               (typeof fieldValue === 'string' && fieldValue.trim() === '');
      case 'isNotEmpty':
        return fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0) && 
               (typeof fieldValue !== 'string' || fieldValue.trim() !== '');
      default:
        return false;
    }
  };

  const evaluateCondition = (): boolean => {
    if (condition.rules.length === 0) return true;
    
    const results = condition.rules.map(evaluateRule);
    
    if (condition.logic === 'or') {
      return results.some(result => result);
    } else {
      // Default to 'and' logic
      return results.every(result => result);
    }
  };

  const isVisible = evaluateCondition();

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
          animate={{ 
            opacity: 1, 
            height: 'auto', 
            marginTop: 'var(--original-margin-top, 0)',
            marginBottom: 'var(--original-margin-bottom, 0)'
          }}
          exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
          transition={{ 
            duration: animationDuration,
            ease: 'easeInOut'
          }}
          className={className}
          style={{ overflow: 'hidden' }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConditionalFields;

// Helper hook for creating conditional field configurations
export const useConditionalFields = () => {
  const createRule = (
    field: string,
    operator: ConditionalFieldRule['operator'],
    value?: any
  ): ConditionalFieldRule => ({
    field,
    operator,
    value
  });

  const createCondition = (
    rules: ConditionalFieldRule[],
    logic: 'and' | 'or' = 'and'
  ): ConditionalFieldConfig => ({
    rules,
    logic
  });

  // Common condition builders
  const when = {
    field: (field: string) => ({
      equals: (value: any) => createRule(field, 'equals', value),
      notEquals: (value: any) => createRule(field, 'notEquals', value),
      contains: (value: string) => createRule(field, 'contains', value),
      greaterThan: (value: number) => createRule(field, 'greaterThan', value),
      lessThan: (value: number) => createRule(field, 'lessThan', value),
      isEmpty: () => createRule(field, 'isEmpty'),
      isNotEmpty: () => createRule(field, 'isNotEmpty')
    })
  };

  return {
    createRule,
    createCondition,
    when
  };
};

// Pre-built common conditions for assistant forms
export const commonConditions = {
  // Show voice settings when voice is enabled
  voiceEnabled: {
    rules: [{ field: 'voiceEnabled', operator: 'equals' as const, value: true }],
    logic: 'and' as const
  },
  
  // Show advanced settings when model is GPT-4
  isAdvancedModel: {
    rules: [{ field: 'model', operator: 'contains' as const, value: 'gpt-4' }],
    logic: 'and' as const
  },
  
  // Show custom tool configuration when tools are selected
  hasTools: {
    rules: [{ field: 'tools', operator: 'isNotEmpty' as const }],
    logic: 'and' as const
  },
  
  // Show knowledge base settings when knowledge base is selected
  hasKnowledgeBase: {
    rules: [{ field: 'knowledgeBaseId', operator: 'isNotEmpty' as const }],
    logic: 'and' as const
  },
  
  // Show calendar integration when calendar is enabled
  calendarEnabled: {
    rules: [{ field: 'calendarEnabled', operator: 'equals' as const, value: true }],
    logic: 'and' as const
  }
};